import { useState } from "react";
import Card from "./Card";
import Toggle from "./Toggle";

const EXPORT_OPTIONS = [
  { key: "patientInfo", label: "Patient information" },
  { key: "snapshot", label: "Overview snapshot" },
  { key: "mood", label: "Mood data" },
  { key: "symptoms", label: "Symptom data" },
  { key: "bleeding", label: "Vaginal bleeding summary" },
  { key: "journal", label: "Journal themes" },
  { key: "activities", label: "Activities" },
  { key: "redFlags", label: "Red-flag history" },
];

interface ExportCardProps {
  toggles: Record<string, boolean>;
  onToggle: (key: string) => void;
  onExportPDF: () => Promise<void>;
}

export default function ExportCard({ toggles, onToggle, onExportPDF }: ExportCardProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await onExportPDF();
    } catch (err) {
      console.error("[ExportCard] PDF generation failed:", err);
      setExportError("Failed to generate PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Card title="Export Options">
        <div className="divide-y divide-stone-100">
          {EXPORT_OPTIONS.map((item) => (
            <div key={item.key} className="flex justify-between items-center py-3">
              <span className="text-sm text-stone-700">{item.label}</span>
              <Toggle on={toggles[item.key] ?? false} onToggle={() => onToggle(item.key)} />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Ready to Share">
        <p className="text-sm text-stone-500 mb-5 leading-relaxed">
          Export a PDF to bring to your GP or physiotherapist appointment.
        </p>
        <button
          onClick={handleExport}
          disabled={isExporting}
          className="w-full flex items-center justify-center gap-2.5 rounded-2xl py-4 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(135deg,#6C7A52 0%,#C6D8A5 100%)",
            boxShadow: "0 4px 20px rgba(108,122,82,0.3)",
            letterSpacing: "0.02em",
          }}
        >
          {isExporting ? (
            <>
              <span className="animate-spin text-lg">{"\u23F3"}</span> Generating PDF...
            </>
          ) : (
            <>
              <span className="text-lg">{"\uD83D\uDCC4"}</span> Export PDF for GP
            </>
          )}
        </button>
        {exportError && (
          <div className="flex gap-2.5 mt-3 p-3 rounded-xl items-start bg-[#F5E8E8]">
            <span className="text-sm flex-shrink-0 mt-0.5">{"\u274C"}</span>
            <p className="text-xs leading-relaxed text-[#8B4040]">{exportError}</p>
          </div>
        )}
        <div className="flex gap-2.5 mt-4 p-3.5 rounded-xl items-start bg-[#FBF3E0]">
          <span className="text-sm flex-shrink-0 mt-0.5">{"\u26A0\uFE0F"}</span>
          <p className="text-xs leading-relaxed text-[#6B5A2B]">
            This summary supports your conversation with your GP. It does not replace professional
            medical advice.
          </p>
        </div>
      </Card>
    </>
  );
}
